/* =============================================================================
   SETTING-HELPERS — Wrapper für Setting-Updates mit Clamping
   =============================================================================
   Session 23 (M-01): Die Settings-View hatte mehrere Inline-Handler à la
     oninput="updateSetting('x', Math.max(MIN, Math.min(MAX, parseInt(this.value,10) || DEF)))"
   Solche Multi-Expression-Inline-Handler lassen sich nicht generisch durch
   den dom-bindings-Parser auflösen (und Inline-JS-Eval verbietet CSP eh).
   Die Logik wandert deshalb in benannte Helper, die der Parser als
   `helperFn(this.value, MIN, MAX, DEF)` versteht.

   Diese Helper hängen sich ans `window`, damit die HTML-String-Templates der
   View-Module sie ohne Import-Boilerplate ansprechen können (gleiche
   Konvention wie der Rest der View-Layer).
============================================================================ */

/**
 * Wandelt einen User-String-Wert in eine geclampte Ganzzahl um und ruft
 * `window.updateSetting(key, value)` damit auf.
 *
 * @param {string} key       Setting-Key (z. B. 'slotDurationMin')
 * @param {*} rawValue       this.value (String aus dem Input)
 * @param {number} min       Untere Grenze
 * @param {number} max       Obere Grenze
 * @param {number} fallback  Default, wenn der String nicht parsbar ist
 */
export function updateSettingClampedInt(key, rawValue, min, max, fallback) {
  let n = parseInt(rawValue, 10);
  if (!Number.isFinite(n)) n = fallback;
  if (typeof min === 'number') n = Math.max(min, n);
  if (typeof max === 'number') n = Math.min(max, n);
  if (typeof window.updateSetting === 'function') {
    window.updateSetting(key, n);
  }
}

/**
 * S244-HF — die ANZEIGE eines geclampten Feldes nachziehen.
 *
 * `updateSettingClampedInt` persistiert den geclampten Wert, lässt aber stehen,
 * was der Operator getippt hat: nach „999" stand im Feld 999, in der DB 240 —
 * bis irgendein Re-Render das Feld zufällig neu zeichnete. Zwei verschiedene
 * Wahrheiten auf demselben Bildschirm.
 *
 * Bewusst für `onchange` (Feld verlassen), NICHT für `oninput`: wer „300" tippt,
 * soll nicht nach der „3" schon korrigiert werden. Das Persistieren bleibt live
 * am `oninput`, die Anzeige wird beim Verlassen normalisiert.
 *
 * @param {HTMLInputElement} el  das Feld (`this` aus dem Inline-Handler)
 */
export function normalizeClampedInput(el, min, max, fallback) {
  if (!el) return;
  let n = parseInt(el.value, 10);
  if (!Number.isFinite(n)) n = fallback;
  if (typeof min === 'number') n = Math.max(min, n);
  if (typeof max === 'number') n = Math.min(max, n);
  if (String(n) !== String(el.value)) el.value = String(n);
}

/**
 * Wie updateSettingClampedInt, aber ohne max-Grenze (für „min only"-Felder
 * wie z. B. Vorlaufzeiten in Minuten).
 */
export function updateSettingMinInt(key, rawValue, min, fallback) {
  let n = parseInt(rawValue, 10);
  if (!Number.isFinite(n)) n = fallback;
  if (typeof min === 'number') n = Math.max(min, n);
  if (typeof window.updateSetting === 'function') {
    window.updateSetting(key, n);
  }
}

/**
 * Setzt ein Setting und ruft danach `window.applyBrandTheme()` auf.
 * Ersetzt das vorherige Inline-Pattern
 *   oninput="updateSetting('brandPrimaryColor', this.value); applyBrandTheme();"
 */
export function updateSettingAndApplyBrand(key, value) {
  if (typeof window.updateSetting === 'function') {
    window.updateSetting(key, value);
  }
  if (typeof window.applyBrandTheme === 'function') {
    window.applyBrandTheme();
  }
}

/**
 * Setzt einen verschachtelten Filter-Pfad (z. B. `resFilter.status`) und
 * triggert den passenden Re-Render. Ersetzt Inline-Pattern wie
 *   onchange="resFilter.status=this.value; rerenderReservations();"
 */
export function setFilterAndRerender(path, value, rerenderFn) {
  const parts = path.split('.');
  let target = window;
  for (let i = 0; i < parts.length - 1; i++) {
    if (target[parts[i]] == null) return;
    target = target[parts[i]];
  }
  target[parts[parts.length - 1]] = value;
  if (typeof window[rerenderFn] === 'function') {
    window[rerenderFn]();
  }
}

/**
 * Schließt das aktuelle Modal und öffnet nach kurzem Delay die
 * Reservierungs-Detail-Ansicht. Ersetzt das Inline-Pattern
 *   onclick="closeModal(); setTimeout(() => openReservationDetail('id'), 120);"
 * Das Delay vermeidet, dass das neue Modal in den Schließ-Animations-Frame
 * des alten gerendert wird.
 */
export function closeModalThenOpenReservationDetail(id, delayMs) {
  if (typeof window.closeModal === 'function') window.closeModal();
  const ms = typeof delayMs === 'number' ? delayMs : 120;
  setTimeout(() => {
    if (typeof window.openReservationDetail === 'function') {
      window.openReservationDetail(id);
    }
  }, ms);
}

/* Ans window hängen — gleiche Konvention wie die anderen View-Helper. */
if (typeof window !== 'undefined') {
  window.updateSettingClampedInt = updateSettingClampedInt;
  window.normalizeClampedInput = normalizeClampedInput;
  window.updateSettingMinInt = updateSettingMinInt;
  window.updateSettingAndApplyBrand = updateSettingAndApplyBrand;
  window.setFilterAndRerender = setFilterAndRerender;
  window.closeModalThenOpenReservationDetail = closeModalThenOpenReservationDetail;
}
